import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardActions, CircularProgress } from "@mui/material";
import RawSurfaceSaveDialog from "./RawSurfaceSaveDialog";
import { applyRawSurfaceFilters } from "../../reducer/RawSurfaceSlice";

/**
 * Raw Surface Analysis's rail footer - pinned below the scrollable Circle Fit Thresholds/Shaft
 * Ranges cards, mirroring how CalculationActions.js pins Scan Analysis's Apply/Apply & Save
 * buttons (see VisualizerContent.js's renderWithRightRail).
 *
 * "Calculate Shim Values" computes directly (applyRawSurfaceFilters, the same preview request the
 * filter cards already trigger on every parameter change) without opening a dialog - the numbers
 * just appear in the Shim Values card. "Save Shim Values" opens RawSurfaceSaveDialog, which now
 * only handles reviewing/adjusting ranges and persisting as a Scan; "Compare Saved Scans" moved to
 * RawSurfaceToolbar.js.
 */
export default function RawSurfaceSaveButton() {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const hasKept = rawSurface.circles.some((c) => c.kept && c.pointCount > 0);
    const rangesAndFeetSet = rawSurface.machineRange && rawSurface.motorRange
        && rawSurface.distanceToFF != null && rawSurface.distanceFtoR != null;
    const calculating = rawSurface.shimStatus === "computing";

    // Only this explicit button click scrolls to the result - the same applyRawSurfaceFilters
    // thunk also fires on every live filter-panel change (Surface Correction/NRS/Focus), and
    // yanking the scroll position away from whatever's being tuned there would fight the whole
    // point of tuning multiple parameters in a row (Kevin, 2026-08-29).
    const handleCalculate = async () => {
        await dispatch(applyRawSurfaceFilters());
        document.getElementById("raw-surface-shim-values-card")
            ?.scrollIntoView({ behavior: "smooth", block: "end" });
    };

    return (
        <>
            <Card>
                <CardActions sx={{ justifyContent: "end", paddingRight: "8px" }}>
                    <Button
                        color="inherit"
                        disabled={!hasKept || !rangesAndFeetSet || calculating}
                        onClick={handleCalculate}
                        startIcon={calculating ? <CircularProgress size={14} /> : undefined}
                    >
                        Calculate Shim Values
                    </Button>
                    {/* Deliberately not gated on shaft ranges: the dialog is where ranges can be
                        typed, so gating entry to it would make that the one thing you cannot reach
                        without dragging. The range requirement is enforced on the dialog's own
                        Save action instead. */}
                    <Button variant="contained" disabled={!hasKept} onClick={() => setOpen(true)}>
                        Save Shim Values&hellip;
                    </Button>
                </CardActions>
            </Card>
            <RawSurfaceSaveDialog open={open} onClose={() => setOpen(false)} />
        </>
    );
}
